const WORK_FLOW_SCOPES = new Set(['all', 'main', 'assigned_branches'])

const WORK_FLOW_SURFACE_ALIASES = {
  extra_fields: 'extra-fields',
}

function hasOwn(value, key) {
  return Boolean(value && Object.prototype.hasOwnProperty.call(value, key))
}

function normalizeSurface(value) {
  return WORK_FLOW_SURFACE_ALIASES[value] || value
}

function capabilityAllowed(value) {
  return value === true || value?.allowed === true
}

export function normalizeWorkFlowAccess(value = {}) {
  const source = value?.flow_access || value || {}
  const hasServerScope = hasOwn(source, 'flow_view_scope')
  const rawScope = hasServerScope ? source.flow_view_scope : source.flowViewScope
  const hasExplicitContract = hasServerScope
    ? rawScope === null || WORK_FLOW_SCOPES.has(rawScope)
    : source.contractAvailable === true && (rawScope === null || WORK_FLOW_SCOPES.has(rawScope))
  const contractAvailable = source.contractAvailable !== false && hasExplicitContract
  const flowViewScope = contractAvailable ? rawScope : 'main'
  const rawSurfaces = source.visible_surfaces || source.visibleSurfaces
  const visibleSurfaces = contractAvailable && Array.isArray(rawSurfaces)
    ? [...new Set(rawSurfaces
      .filter(surface => typeof surface === 'string')
      .map(normalizeSurface))]
    : ['flow']

  return {
    contractAvailable,
    flowViewScope,
    visibleSurfaces: flowViewScope === 'assigned_branches' ? ['flow'] : visibleSurfaces,
    capabilities: source.capabilities || {},
  }
}

export function workFlowShowsMain(access = {}) {
  return access.flowViewScope === 'all' || access.flowViewScope === 'main'
}

export function workFlowShowsAssignedBranches(access = {}) {
  return access.flowViewScope === 'assigned_branches'
}

export function workFlowCanViewAssignedBranches(access = {}) {
  return capabilityAllowed(access.capabilities?.can_view_assigned_branch_flows)
}

export function workFlowSurface(access = {}, { preferAssigned = false } = {}) {
  if (!access.contractAvailable) return 'main'
  const canViewAssigned = workFlowCanViewAssignedBranches(access)
  if (preferAssigned && canViewAssigned) return 'assigned'
  if (workFlowShowsMain(access)) return 'main'
  if (workFlowShowsAssignedBranches(access) && canViewAssigned) return 'assigned'
  return 'denied'
}

export function workNodeCan(item = {}, capability) {
  return capabilityAllowed(item.capabilities?.[capability])
}

export function workRuleCanViewBranches(item = {}) {
  return workNodeCan(item, 'can_view_branch_flow')
}

export function visibleWorkDetailTabs(tabs = [], access = {}) {
  if (!access.contractAvailable) return [...tabs]
  const visible = new Set(access.visibleSurfaces || ['flow'])
  return tabs.filter(tab => tab.value !== 'runtime' && visible.has(tab.value))
}

export function normalizeWorkFlowQuery(query = {}, access = {}) {
  if (!access.contractAvailable) {
    return {
      ...query,
      tab: query.tab === 'items' ? 'flow' : query.tab || 'flow',
    }
  }
  const requestedTab = query.tab === 'runtime' ? 'flow' : query.tab || 'flow'
  const visibleSurfaces = access.visibleSurfaces || []
  const next = {
    ...query,
    tab: visibleSurfaces.includes(requestedTab)
      ? requestedTab
      : visibleSurfaces[0] || 'flow',
  }

  if (access.flowViewScope !== 'all') {
    delete next.runtime_node_id
    delete next.execution_id
    delete next.rule_run_id
    delete next.join_id
  }

  if (access.flowViewScope !== 'assigned_branches') return next

  return {
    tab: 'flow',
    ...(next.branch_id ? { branch_id: next.branch_id } : {}),
    ...(next.branch_item_id ? { branch_item_id: next.branch_item_id } : {}),
  }
}
